import { useEffect, useMemo, useRef, useState } from 'react';
import {
  BookOpen,
  ChevronDown,
  ChevronRight,
  Plus,
  Sparkles,
  Flag,
  AlertCircle,
} from 'lucide-react';
import { useFlashcardLibrary } from '../../../hooks/useFlashcardLibrary';
import { useFlashcardGenerationJob } from '../../../hooks/useFlashcardGenerationJob';
import { useFlashcardDecks } from '../../../hooks/useFlashcards';
import { useAlert } from '../../../contexts/AlertContext';
import FlashcardReportDialog from './FlashcardReportDialog';
import type {
  FlashcardLibraryCardDto,
  FlashcardReportReason,
} from '../../../api/flashcardLibraryService';
import { useTranslation } from '../../../i18n/useTranslation';

interface Props {
  bookId: string;
  secureContentChunkId: string | null;
  /** Hard ceiling on library cards per chapter (UI clamp only; backend enforces real limit). */
  maxCardsPerChapter?: number;
  /** Batch size per Generate More click. */
  cardsPerGenerateClick?: number;
}

const targetDeckStorageKey = (bookId: string) =>
  `flashcard-library:target-deck:${bookId}`;

/**
 * Library tab — shows AI-generated flashcards for the current chapter.
 * Users can expand cards, add them to their active deck, or report them.
 */
export default function FlashcardLibraryTab({
  bookId,
  secureContentChunkId,
  maxCardsPerChapter = 30,
  cardsPerGenerateClick = 10,
}: Props) {
  const { t } = useTranslation();
  const { showToast } = useAlert();

  // Bootstrap-generation guard: auto-fire once per chunk on first empty visit.
  const bootstrappedChunks = useRef(new Set<string>());

  const { cards, isLoading, refetch, addToDeck, report, isAddingToDeck } =
    useFlashcardLibrary(bookId, secureContentChunkId);

  // Decks for this book + inline create flow.
  const { decks, createDeck, isCreating } = useFlashcardDecks(bookId);

  // Which deck new cards go into. Persisted per-book in localStorage so the
  // choice follows the student across sessions. Default: most recently created
  // deck (last in the list — `useFlashcardDecks` orders by creationTime asc).
  const [selectedDeckId, setSelectedDeckIdState] = useState<string | null>(null);
  const setSelectedDeckId = (id: string | null) => {
    setSelectedDeckIdState(id);
    if (typeof window !== 'undefined') {
      if (id) window.localStorage.setItem(targetDeckStorageKey(bookId), id);
      else window.localStorage.removeItem(targetDeckStorageKey(bookId));
    }
  };

  // Resolve selection whenever decks change (first load / after create / after delete).
  useEffect(() => {
    if (decks.length === 0) {
      if (selectedDeckId !== null) setSelectedDeckIdState(null);
      return;
    }
    const stored =
      typeof window !== 'undefined'
        ? window.localStorage.getItem(targetDeckStorageKey(bookId))
        : null;
    const storedStillValid = stored && decks.some((d) => d.id === stored);
    const nextId = storedStillValid
      ? stored
      : decks[decks.length - 1].id; // most recently created
    if (nextId !== selectedDeckId) setSelectedDeckIdState(nextId);
  }, [decks, bookId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Picker open/close state.
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!pickerOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (!pickerRef.current?.contains(e.target as Node)) setPickerOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [pickerOpen]);

  // Inline new-deck creation.
  const [newDeckName, setNewDeckName] = useState('');
  const [showNewDeckInput, setShowNewDeckInput] = useState(false);
  const handleCreateDeck = async () => {
    const name = newDeckName.trim();
    if (!name) return;
    try {
      const deck = await createDeck({ bookId, name });
      // New deck becomes the selection automatically.
      setSelectedDeckId(deck.id);
      setNewDeckName('');
      setShowNewDeckInput(false);
      setPickerOpen(false);
      showToast(`Deck "${name}" created.`, { type: 'success' });
    } catch (err: any) {
      const msg =
        err?.response?.data?.error?.message ||
        err?.message ||
        'Failed to create deck.';
      showToast(msg, { type: 'error' });
    }
  };

  const selectedDeckName = useMemo(
    () => decks.find((d) => d.id === selectedDeckId)?.name ?? null,
    [decks, selectedDeckId],
  );

  const { startGeneration, activeJob, isStarting, isPolling, error: jobError } =
    useFlashcardGenerationJob(() => {
      // When a job terminates, refresh the library.
      refetch();
    });

  // Bootstrap: if a chunk is selected, no cards exist, and nothing is loading / running,
  // request a first batch automatically (first visit UX).
  useEffect(() => {
    if (!secureContentChunkId) return;
    if (isLoading) return;
    if (cards.length > 0) return;
    if (isStarting || isPolling) return;
    if (bootstrappedChunks.current.has(secureContentChunkId)) return;

    bootstrappedChunks.current.add(secureContentChunkId);
    startGeneration({
      bookId,
      secureContentChunkId,
      requestedCount: cardsPerGenerateClick,
    }).catch((err) => {
      console.error('Auto-generation failed:', err);
    });
  }, [
    secureContentChunkId,
    isLoading,
    cards.length,
    isStarting,
    isPolling,
    bookId,
    cardsPerGenerateClick,
    startGeneration,
  ]);

  // Reset expanded state when chunk changes
  const [expandedId, setExpandedId] = useState<string | null>(null);
  useEffect(() => {
    setExpandedId(null);
  }, [secureContentChunkId]);

  // Report dialog state
  const [reportingCard, setReportingCard] = useState<FlashcardLibraryCardDto | null>(null);

  const chapterTitle = useMemo(() => {
    const firstWithTitle = cards.find((c) => c.chapterTitle);
    return firstWithTitle?.chapterTitle ?? null;
  }, [cards]);

  const slotsLeft = Math.max(0, maxCardsPerChapter - cards.length);
  const atCap = cards.length >= maxCardsPerChapter;
  const generationInProgress = isStarting || isPolling;

  // Placeholder: no chunk selected
  if (!secureContentChunkId) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-6 text-center">
        <BookOpen className="w-12 h-12 text-neutral-300 mb-3" aria-hidden="true" />
        <p className="text-sm text-neutral-500 max-w-[260px]">
          {t('App.Reader.Flashcard.Library.NoChapterSelected')}
        </p>
      </div>
    );
  }

  const handleAddToDeck = async (card: FlashcardLibraryCardDto) => {
    if (!selectedDeckId) {
      showToast('Create a deck first.', { type: 'info' });
      return;
    }
    try {
      await addToDeck({ libraryCardId: card.id, deckId: selectedDeckId });
      showToast(
        selectedDeckName
          ? `Added to "${selectedDeckName}".`
          : 'Added to deck.',
        { type: 'success' },
      );
    } catch (err: any) {
      const msg =
        err?.response?.data?.error?.message ||
        err?.message ||
        'Failed to add card to deck.';
      showToast(msg, { type: 'error' });
    }
  };

  const handleSubmitReport = async (
    reason: FlashcardReportReason,
    notes?: string,
  ) => {
    if (!reportingCard) return;
    try {
      await report({
        libraryCardId: reportingCard.id,
        reason,
        notes,
      });
      showToast('Report submitted. Thanks!', { type: 'success' });
    } catch (err: any) {
      const msg =
        err?.response?.data?.error?.message ||
        err?.message ||
        'Failed to submit report.';
      showToast(msg, { type: 'error' });
    }
  };

  const handleGenerateMore = async () => {
    if (!secureContentChunkId) return;
    try {
      await startGeneration({
        bookId,
        secureContentChunkId,
        requestedCount: cardsPerGenerateClick,
      });
    } catch (err: any) {
      const msg =
        err?.response?.data?.error?.message ||
        err?.message ||
        t('App.Reader.Flashcard.Library.GenerationError');
      showToast(msg, { type: 'error' });
    }
  };

  return (
    <div className="h-full flex flex-col" style={{ width: '100%', padding: '16px' }}>
      {/* Header */}
      <div className="mb-3">
        {chapterTitle && (
          <h3 className="text-sm font-semibold text-neutral-800 truncate" title={chapterTitle}>
            {chapterTitle}
          </h3>
        )}
        <p className="text-xs text-neutral-600 mt-1">
          {t('App.Reader.Flashcard.Library.CardCount', { count: cards.length })}
          {' · '}
          {t('App.Reader.Flashcard.Library.SlotsLeft', { slots: slotsLeft })}
        </p>
      </div>

      {/* Error surface — prefer ABP business-exception message over axios's generic "Request failed with status code N". */}
      {jobError && (
        <div
          role="alert"
          className="mb-3 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-800"
        >
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
          <span>
            {((jobError as any)?.response?.data?.error?.message)
              || (jobError as Error).message
              || t('App.Reader.Flashcard.Library.GenerationError')}
          </span>
        </div>
      )}

      {/* Generation job surface — only when NO cards exist yet (true bootstrap). */}
      {/* Once the library has cards, the Generate More button already shows "Generating…" state — the banner above it would be duplicate noise. */}
      {generationInProgress && cards.length === 0 && (
        <div
          role="status"
          aria-live="polite"
          className="mb-3 flex items-center gap-2 rounded-md border border-primary-200 bg-primary-50 p-2 text-xs text-primary-800"
        >
          <Sparkles className="w-4 h-4 flex-shrink-0 animate-pulse" aria-hidden="true" />
          <span>
            {activeJob?.status === 'Running'
              ? t('App.Reader.Flashcard.Library.Generating')
              : t('App.Reader.Flashcard.Library.Queued')}
          </span>
        </div>
      )}

      {/* Deck selector — chooses which deck Add-to-deck targets. Persists per book. */}
      <div className="mb-3" ref={pickerRef}>
        {decks.length === 0 ? (
          // No decks yet: inline create form replaces the picker.
          showNewDeckInput ? (
            <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 p-2">
              <input
                type="text"
                value={newDeckName}
                onChange={(e) => setNewDeckName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleCreateDeck(); }}
                placeholder="Deck name"
                className="flex-1 min-h-[36px] rounded border border-neutral-300 px-2 py-1 text-sm"
                autoFocus
              />
              <button
                onClick={handleCreateDeck}
                disabled={isCreating || !newDeckName.trim()}
                className="min-h-[36px] px-3 rounded bg-primary-600 text-white text-sm disabled:opacity-50"
              >
                {isCreating ? '…' : 'Create'}
              </button>
              <button
                onClick={() => { setShowNewDeckInput(false); setNewDeckName(''); }}
                className="min-h-[36px] px-2 rounded text-sm text-neutral-600"
                aria-label="Cancel"
              >
                ✕
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowNewDeckInput(true)}
              className="w-full flex items-center justify-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900 hover:bg-amber-100"
            >
              <AlertCircle className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
              <span dangerouslySetInnerHTML={{ __html: t('App.Reader.Flashcard.Library.NoDeckWarning') }} />
            </button>
          )
        ) : (
          // At least one deck exists: selector chip.
          <div className="relative">
            <button
              onClick={() => setPickerOpen(!pickerOpen)}
              className="w-full min-h-[40px] flex items-center justify-between gap-2 rounded-md border border-neutral-300 bg-white px-3 py-1.5 text-sm hover:bg-neutral-50"
              aria-haspopup="listbox"
              aria-expanded={pickerOpen}
            >
              <span className="text-neutral-600 text-xs">{t('App.Reader.Flashcard.Library.AddingToLabel')}</span>
              <span className="flex-1 text-left font-medium text-neutral-900 truncate">
                {selectedDeckName ?? t('App.Reader.Flashcard.Library.PickDeckPlaceholder')}
              </span>
              <ChevronDown className="w-4 h-4 text-neutral-400 flex-shrink-0" aria-hidden="true" />
            </button>

            {pickerOpen && (
              <div
                role="listbox"
                className="absolute z-10 top-full left-0 right-0 mt-1 max-h-64 overflow-y-auto rounded-md border border-neutral-200 bg-white shadow-lg"
              >
                {decks.map((d) => (
                  <button
                    key={d.id}
                    role="option"
                    aria-selected={d.id === selectedDeckId}
                    onClick={() => { setSelectedDeckId(d.id); setPickerOpen(false); }}
                    className={`w-full min-h-[40px] flex items-center px-3 py-2 text-sm text-left hover:bg-neutral-50 ${d.id === selectedDeckId ? 'bg-primary-50 text-primary-800 font-medium' : ''}`}
                  >
                    <span className="truncate">{d.name}</span>
                  </button>
                ))}
                <div className="border-t border-neutral-200">
                  {showNewDeckInput ? (
                    <div className="flex items-center gap-2 p-2">
                      <input
                        type="text"
                        value={newDeckName}
                        onChange={(e) => setNewDeckName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleCreateDeck(); }}
                        placeholder="New deck name"
                        className="flex-1 min-h-[32px] rounded border border-neutral-300 px-2 py-1 text-sm"
                        autoFocus
                      />
                      <button
                        onClick={handleCreateDeck}
                        disabled={isCreating || !newDeckName.trim()}
                        className="min-h-[32px] px-2 rounded bg-primary-600 text-white text-xs disabled:opacity-50"
                      >
                        {isCreating ? '…' : 'Save'}
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowNewDeckInput(true)}
                      className="w-full min-h-[40px] flex items-center gap-2 px-3 py-2 text-sm text-primary-700 hover:bg-primary-50"
                    >
                      <Plus className="w-4 h-4" aria-hidden="true" />
                      {t('App.Reader.Flashcard.Library.NewDeckOption')}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="text-center text-sm text-neutral-500 py-8">{t('App.Reader.Flashcard.Library.Loading')}</div>
        ) : cards.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Sparkles className="w-10 h-10 text-neutral-300 mb-3" aria-hidden="true" />
            <p className="text-sm text-neutral-500 max-w-[260px]">
              {generationInProgress
                ? t('App.Reader.Flashcard.Library.EmptyGenerating')
                : t('App.Reader.Flashcard.Library.EmptyNoGeneration')}
            </p>
          </div>
        ) : (
          <ul className="flex flex-col gap-2" role="list">
            {cards.map((card) => (
              <LibraryCardRow
                key={card.id}
                card={card}
                expanded={expandedId === card.id}
                onToggle={() =>
                  setExpandedId(expandedId === card.id ? null : card.id)
                }
                onAdd={() => handleAddToDeck(card)}
                onReport={() => setReportingCard(card)}
                addDisabled={!selectedDeckId || isAddingToDeck}
              />
            ))}
          </ul>
        )}
      </div>

      {/* Footer — generation is independent of personal decks: anyone can grow the shared library. */}
      <div className="pt-3 mt-2 border-t border-neutral-200">
        <button
          onClick={handleGenerateMore}
          disabled={atCap || generationInProgress}
          className="w-full min-h-[44px] inline-flex items-center justify-center gap-2 rounded-md bg-primary-600 text-white text-sm font-medium px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary-700 transition-colors"
          aria-label={t('App.Reader.Flashcard.Library.GenerateMoreAriaLabel', { count: cardsPerGenerateClick })}
        >
          <Sparkles className="w-4 h-4" aria-hidden="true" />
          {generationInProgress
            ? t('App.Reader.Flashcard.Library.Generating')
            : atCap
            ? t('App.Reader.Flashcard.Library.AtCapLabel')
            : t('App.Reader.Flashcard.Library.GenerateMoreButton', { count: cardsPerGenerateClick })}
        </button>
      </div>

      {/* Report dialog */}
      <FlashcardReportDialog
        open={!!reportingCard}
        onClose={() => setReportingCard(null)}
        onSubmit={handleSubmitReport}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

interface RowProps {
  card: FlashcardLibraryCardDto;
  expanded: boolean;
  onToggle: () => void;
  onAdd: () => void;
  onReport: () => void;
  addDisabled: boolean;
}

function LibraryCardRow({ card, expanded, onToggle, onAdd, onReport, addDisabled }: RowProps) {
  const { t } = useTranslation();

  const typeLabel =
    card.cardType === 'QuestionAnswer'
      ? t('App.Reader.Flashcard.Library.TypeQA')
      : card.cardType === 'Cloze'
      ? t('App.Reader.Flashcard.Library.TypeCloze')
      : t('App.Reader.Flashcard.Library.TypeMCQ');

  const typeClass =
    card.cardType === 'QuestionAnswer'
      ? 'bg-neutral-100 text-neutral-700'
      : card.cardType === 'Cloze'
      ? 'bg-amber-100 text-amber-800'
      : 'bg-indigo-100 text-indigo-800';

  const distractorCount = card.distractors?.length ?? 0;

  return (
    <li className="border border-neutral-200 rounded-md bg-white overflow-hidden">
      <div className="flex items-start gap-2 p-2">
        <button
          onClick={onToggle}
          className="min-h-[44px] min-w-[32px] flex items-start justify-center pt-1 text-neutral-500"
          aria-label={expanded ? t('App.Reader.Flashcard.Library.CollapseCard') : t('App.Reader.Flashcard.Library.ExpandCard')}
          aria-expanded={expanded}
        >
          {expanded ? (
            <ChevronDown className="w-4 h-4" aria-hidden="true" />
          ) : (
            <ChevronRight className="w-4 h-4" aria-hidden="true" />
          )}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span
              className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${typeClass}`}
            >
              {typeLabel}
            </span>
            {card.cardType === 'MultipleChoice' && distractorCount > 0 && (
              <span className="text-[10px] text-neutral-500">
                {t('App.Reader.Flashcard.Library.DistractorCount', { count: distractorCount })}
              </span>
            )}
          </div>
          <p className="text-sm text-neutral-800 leading-snug break-words">
            {card.question}
          </p>
          {expanded && <ExpandedDetail card={card} />}
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 px-2 pb-2">
        <button
          onClick={onReport}
          className="min-h-[44px] inline-flex items-center gap-1 text-xs text-neutral-500 px-2 py-2 rounded hover:bg-neutral-100"
          aria-label={t('App.Reader.Flashcard.Library.ReportAriaLabel')}
          title="Report"
        >
          <Flag className="w-4 h-4" aria-hidden="true" />
          <span className="sr-only sm:not-sr-only">Report</span>
        </button>
        <button
          onClick={onAdd}
          disabled={addDisabled}
          className="min-h-[44px] inline-flex items-center gap-1 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-2 rounded"
          aria-label={t('App.Reader.Flashcard.Library.AddAriaLabel')}
        >
          <Plus className="w-4 h-4" aria-hidden="true" />
          Add to deck
        </button>
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Expanded detail (varies by card type)
// ---------------------------------------------------------------------------

function ExpandedDetail({ card }: { card: FlashcardLibraryCardDto }) {
  const { t } = useTranslation();

  if (card.cardType === 'QuestionAnswer') {
    return (
      <div className="mt-2 pt-2 border-t border-neutral-100">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500 mb-1">
          {t('App.Reader.Flashcard.Library.AnswerLabel')}
        </p>
        <p className="text-sm text-neutral-700 leading-snug break-words">{card.answer}</p>
      </div>
    );
  }

  if (card.cardType === 'Cloze') {
    return (
      <div className="mt-2 pt-2 border-t border-neutral-100">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500 mb-1">
          {t('App.Reader.Flashcard.Library.FullSentenceLabel')}
        </p>
        <p className="text-sm text-neutral-700 leading-snug break-words">
          {card.clozeText ?? card.answer}
        </p>
      </div>
    );
  }

  // MultipleChoice
  const options = [card.answer, ...(card.distractors ?? [])];
  return (
    <div className="mt-2 pt-2 border-t border-neutral-100">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500 mb-1">
        {t('App.Reader.Flashcard.Library.OptionsLabel')}
      </p>
      <ul className="space-y-1">
        {options.map((opt, i) => (
          <li
            key={`${opt}-${i}`}
            className={`text-sm leading-snug break-words ${
              i === 0 ? 'text-green-700 font-medium' : 'text-neutral-700'
            }`}
          >
            {i === 0 ? '✓ ' : '• '}
            {opt}
          </li>
        ))}
      </ul>
    </div>
  );
}
